import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars } from "../components/FourStars";
import { api } from "../lib/api";

export default function DonationSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current < 7) {
          attempts.current += 1;
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      } catch {
        if (attempts.current < 5) {
          attempts.current += 1;
          setTimeout(poll, 2000);
        } else {
          setStatus("failed");
        }
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 stars-pattern" data-testid="donation-success-page">
      <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-[#D4AF37]/30 p-10 text-center">
        <FourStars size={14} className="justify-center mb-5" />
        {status === "checking" && (
          <>
            <Loader2 size={56} className="text-[#0052CC] mx-auto animate-spin" />
            <h1 className="font-display text-3xl text-[#0A0F1A] mt-5">Vérification du paiement...</h1>
            <p className="text-[#475569] mt-2">Quelques instants, nous confirmons votre transaction.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto">
              <CheckCircle2 size={48} className="text-[#D4AF37]" />
            </div>
            <h1 className="font-display text-3xl text-[#0A0F1A] mt-5" data-testid="success-title">Merci pour votre soutien !</h1>
            <p className="text-[#475569] mt-3 leading-relaxed">
              Votre don de <strong className="text-[#0A0F1A]">${info?.amount}</strong> {info?.label && (<>— {info.label}</>)} a bien été enregistré.
              Ensemble, nous bâtissons un Congo plus fort.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Button asChild className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="success-dashboard-btn">
                <Link to="/dashboard">Mon espace membre</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm">
                <Link to="/">Retour à l'accueil</Link>
              </Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle size={56} className="text-[#CE1126] mx-auto" />
            <h1 className="font-display text-3xl text-[#0A0F1A] mt-5">Paiement non confirmé</h1>
            <p className="text-[#475569] mt-2">
              {status === "expired" ? "La session a expiré." : "Votre paiement n'a pas pu être vérifié."} Vous pouvez réessayer.
            </p>
            <Button asChild className="mt-6 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm">
              <Link to="/dons"><Heart size={16} className="mr-2" /> Réessayer</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
